import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { range } from '@/utils';

import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import { DemoToggleButtonGroupControl } from '@/components/Demo';

import GridDemo from './GridDemo';
import BaseItem from './Item';
import {
  GridStructure,
  BaseCodeWrapper,
  LabeledColumn,
} from './Grid.shared';

function JustifyContent({ numOfChildren = 2 }) {
  return (
    <GridDemo
      initialValues={{
        justifyContent: 'start',
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      defaultPerspectiveEnabled={false}
      controls={[
        <ControlWrapper key="justifyContent">
          <DemoToggleButtonGroupControl
            id="justifyContent"
            label="justify-content"
            options={[
              'start',
              'center',
              'end',
              'space-between',
              'space-around',
              'space-evenly',
            ]}
          />
        </ControlWrapper>,
      ]}
    >
      {({ justifyContent, isPerspectiveEnabled }) => {
        const gridTemplateColumns = '90px 90px';
        const cssCode = `\
.parent {
  display: grid;
  grid-template-columns: ${gridTemplateColumns};
  justify-content: ${justifyContent};
}`;
        return (
          <Row>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
            <Wrapper layout layoutRoot>
              <BackLayer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <GridStructure
                  style={{
                    gridTemplateColumns,
                    justifyContent,
                    gap: 2,
                  }}
                >
                  <LabeledColumn layout={!isPerspectiveEnabled}>
                    C1
                  </LabeledColumn>
                  <LabeledColumn layout={!isPerspectiveEnabled}>
                    C2
                  </LabeledColumn>
                </GridStructure>
              </BackLayer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper
                  style={{
                    gridTemplateColumns,
                    justifyContent,
                    justifyItems: 'center',
                  }}
                >
                  {range(numOfChildren).map((index) => (
                    <Item
                      key={index}
                      layout={!isPerspectiveEnabled}
                      style={{
                        opacity: isPerspectiveEnabled ? 0.8 : 1,
                        width: 82,
                      }}
                    />
                  ))}
                </ChildWrapper>
              </Layer>
            </Wrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const ControlWrapper = styled.div`
  width: 100%;
  padding-bottom: 16px;
`;

const Row = styled.div`
  display: flex;
  gap: 24px;
  margin: 48px 0px 16px;
  isolation: isolate;

  @media (max-width: 47.5rem) {
    flex-direction: column-reverse;
    gap: 32px;
    align-items: center;
    margin: 60px 0px 16px;
  }
`;

const Wrapper = styled(motion.div)`
  position: relative;
  width: 260px;
  max-width: calc(100vw - 32px);
  min-height: 190px;

  @media (max-width: 47.5rem) {
    width: 386px;
  }
`;

const Layer = styled(motion.div)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const BackLayer = styled(Layer)`
  background: repeating-linear-gradient(
    45deg,
    hsl(210deg 40% 80% / 0.06) 0px,
    hsl(210deg 40% 80% / 0.06) 10px,
    hsl(210deg 40% 80% / 0) 10px,
    hsl(210deg 40% 80% / 0) 20px
  );
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 6px 2px;
  padding: 2px;
`;

const Item = styled(BaseItem)`
  margin: 0;
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  position: relative;
  z-index: 2;
  width: 388px;
  flex-grow: 1;

  @media (max-width: 27.5rem) {
    width: calc(100% + 32px);
    max-width: calc(100% + 32px);
    margin-inline: -16px;
    padding-inline: 3px;
    border-radius: 0;
  }
`;

export default JustifyContent;
